import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { RequisitionBadge, FefoBadge, ExpiryBadge } from "@/components/StatusBadges";
import ExportDialog from "@/components/ExportDialog";
import { ClipboardCheck, Send, CheckCircle2, XCircle, PackageCheck, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

const REQUESTER_ROLES = ["technician", "center_staff", "field_staff"];
const APPROVER_ROLES = ["store_manager", "procurement_officer"];

export default function Requisitions() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isRequester = REQUESTER_ROLES.includes(user.role) || user.role === "store_manager" || user.role === "super_admin";
  const isApprover = APPROVER_ROLES.includes(user.role) || user.role === "super_admin";
  const isIssuer = user.role === "store_manager" || user.role === "super_admin";

  const { data: requisitions = [] } = useQuery({ queryKey: ["requisitions"], queryFn: () => api.get("/requisitions").then((r) => r.data) });
  const { data: items = [] } = useQuery({ queryKey: ["items", "consumable"], queryFn: () => api.get("/items?kind=consumable").then((r) => r.data) });
  const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: () => api.get("/locations").then((r) => r.data) });

  // ---- New requisition form ----
  const blankReq = { item_id: "", location_id: user.location_id || "", qty_requested: 1, notes: "" };
  const [form, setForm] = useState(blankReq);

  const submitRequisition = async () => {
    if (!form.item_id || !form.location_id) { toast.error("Select an item and location"); return; }
    try {
      await api.post("/requisitions", { ...form, qty_requested: Number(form.qty_requested) });
      toast.success("Requisition submitted for approval");
      setForm(blankReq);
      qc.invalidateQueries({ queryKey: ["requisitions"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- Approve / Reject / Issue / Receive action dialog ----
  const [action, setAction] = useState(null); // { type: 'approve'|'reject'|'issue', req }
  const [qtyApproved, setQtyApproved] = useState(1);
  const [approveNotes, setApproveNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [issueLocationId, setIssueLocationId] = useState("");
  const [issueBatchId, setIssueBatchId] = useState("");
  const [issueQty, setIssueQty] = useState(1);

  const openAction = (type, req) => {
    setAction({ type, req });
    if (type === "approve") { setQtyApproved(req.qty_requested); setApproveNotes(""); }
    if (type === "reject") setRejectReason("");
    if (type === "issue") {
      setIssueLocationId(locations[0]?._id || "");
      setIssueBatchId("");
      setIssueQty(req.qty_approved || req.qty_requested);
    }
  };
  const closeAction = () => setAction(null);

  const { data: issueBatches = [] } = useQuery({
    queryKey: ["batches", action?.req?.item_id, issueLocationId],
    queryFn: () => api.get(`/inventory/batches?item_id=${action.req.item_id}&location_id=${issueLocationId}`).then((r) => r.data),
    enabled: !!(action?.type === "issue" && issueLocationId),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["requisitions"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["inventory"] });
    qc.invalidateQueries({ queryKey: ["alerts"] });
  };

  const doApprove = async () => {
    try {
      await api.put(`/requisitions/${action.req._id}/approve`, { qty_approved: Number(qtyApproved), notes: approveNotes });
      toast.success("Requisition approved");
      closeAction(); invalidateAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doReject = async () => {
    if (!rejectReason.trim()) { toast.error("A reason is required"); return; }
    try {
      await api.put(`/requisitions/${action.req._id}/reject`, { reason: rejectReason });
      toast.success("Requisition rejected");
      closeAction(); invalidateAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doIssue = async () => {
    if (!issueBatchId) { toast.error("Select a batch to issue from"); return; }
    try {
      await api.put(`/requisitions/${action.req._id}/issue`, { batch_id: issueBatchId, qty: Number(issueQty) });
      toast.success("Requisition issued — stock updated");
      closeAction(); invalidateAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const doReceive = async (req) => {
    try {
      await api.put(`/requisitions/${req._id}/receive`);
      toast.success("Receipt confirmed");
      invalidateAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const canReceive = (r) => r.status === "issued" && (r.requested_by === user._id || isIssuer);

  return (
    <div className="space-y-5" data-testid="requisitions-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-3xl tracking-tight text-navy">Requisitions</h2>
          <p className="text-slate-500 text-sm mt-1">
            {isApprover || user.role === "super_admin"
              ? "Review, approve and issue requests raised by field teams."
              : "Request items you need — Store Manager will approve and issue."}
          </p>
        </div>
        <ExportDialog dataset="requisitions" testid="requisitions-export" />
      </div>

      {isRequester && (
        <Card className="p-6 bg-white border-slate-200 max-w-3xl">
          <div className="flex items-center gap-2 mb-4 text-navy">
            <Send size={18} /> <span className="font-serif text-lg tracking-tight">New Requisition</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Item</Label>
              <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                <SelectTrigger data-testid="requisition-item"><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {items.map((i) => <SelectItem key={i._id} value={i._id}>{i.name} ({i.category})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location (where it's needed)</Label>
              <Select value={form.location_id} onValueChange={(v) => setForm({ ...form, location_id: v })}>
                <SelectTrigger data-testid="requisition-location"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {locations.map((l) => <SelectItem key={l._id} value={l._id}>{l.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Quantity</Label>
              <Input type="number" min={1} data-testid="requisition-qty" value={form.qty_requested} onChange={(e) => setForm({ ...form, qty_requested: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>Reason / Notes (optional)</Label>
              <Textarea data-testid="requisition-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. running low ahead of Monday clinic load" />
            </div>
          </div>
          <Button onClick={submitRequisition} data-testid="requisition-submit" className="mt-5 text-white" style={{ background: "#042C53" }}>
            <Send size={16} className="mr-2" /> Submit Requisition
          </Button>
        </Card>
      )}

      <div>
        <h3 className="font-serif text-2xl tracking-tight text-navy mb-3">
          {isApprover || user.role === "super_admin" ? "All Requisitions" : "My Requisitions"}
        </h3>
        <Card className="bg-white border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Item</th>
                  <th className="px-4 py-3 font-semibold">Location</th>
                  <th className="px-4 py-3 font-semibold text-right">Qty Req.</th>
                  <th className="px-4 py-3 font-semibold text-right">Qty Appr.</th>
                  <th className="px-4 py-3 font-semibold">Requested By</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {requisitions.map((r, idx) => (
                  <tr key={r._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`requisition-row-${idx}`}>
                    <td className="px-4 py-3 text-slate-600">{new Date(r.created_at).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                    <td className="px-4 py-3 font-medium text-navy">{r.item_name}</td>
                    <td className="px-4 py-3 text-slate-600">{r.location_name}</td>
                    <td className="px-4 py-3 text-right">{r.qty_requested} {r.uom}</td>
                    <td className="px-4 py-3 text-right">{r.qty_approved ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.requested_by_name}</td>
                    <td className="px-4 py-3"><RequisitionBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {r.status === "pending" && isApprover && (
                        <div className="flex gap-1.5 justify-end">
                          <Button size="sm" variant="outline" data-testid={`requisition-approve-${idx}`}
                            className="border-[#10B981] text-[#10B981] hover:bg-[#10B981] hover:text-white"
                            onClick={() => openAction("approve", r)}>
                            <ThumbsUp size={14} className="mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`requisition-reject-${idx}`}
                            className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white"
                            onClick={() => openAction("reject", r)}>
                            <XCircle size={14} className="mr-1" /> Reject
                          </Button>
                        </div>
                      )}
                      {r.status === "approved" && isIssuer && (
                        <Button size="sm" data-testid={`requisition-issue-${idx}`} className="text-white" style={{ background: "#6366F1" }}
                          onClick={() => openAction("issue", r)}>
                          <PackageCheck size={14} className="mr-1" /> Issue
                        </Button>
                      )}
                      {canReceive(r) && (
                        <Button size="sm" data-testid={`requisition-receive-${idx}`} className="text-white" style={{ background: "#10B981" }}
                          onClick={() => doReceive(r)}>
                          <CheckCircle2 size={14} className="mr-1" /> Confirm Received
                        </Button>
                      )}
                      {r.status === "rejected" && r.rejected_reason && (
                        <span className="text-xs text-slate-400 italic">Reason: {r.rejected_reason}</span>
                      )}
                    </td>
                  </tr>
                ))}
                {requisitions.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No requisitions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Approve dialog */}
      <Dialog open={action?.type === "approve"} onOpenChange={(o) => !o && closeAction()}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-navy text-xl">Approve Requisition</DialogTitle></DialogHeader>
          {action?.req && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{action.req.item_name} — requested by {action.req.requested_by_name} for {action.req.location_name}</p>
              <div>
                <Label>Quantity to Approve</Label>
                <Input type="number" min={1} data-testid="approve-qty" value={qtyApproved} onChange={(e) => setQtyApproved(e.target.value)} />
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea value={approveNotes} onChange={(e) => setApproveNotes(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={doApprove} data-testid="approve-confirm" className="text-white" style={{ background: "#10B981" }}>
              <ThumbsUp size={16} className="mr-2" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={action?.type === "reject"} onOpenChange={(o) => !o && closeAction()}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-navy text-xl">Reject Requisition</DialogTitle></DialogHeader>
          {action?.req && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{action.req.item_name} — requested by {action.req.requested_by_name}</p>
              <div>
                <Label>Reason (required)</Label>
                <Textarea data-testid="reject-reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="e.g. sufficient stock already at this location" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={doReject} data-testid="reject-confirm" className="text-white" style={{ background: "#EF4444" }}>
              <XCircle size={16} className="mr-2" /> Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue dialog */}
      <Dialog open={action?.type === "issue"} onOpenChange={(o) => !o && closeAction()}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-navy text-xl">Issue Stock</DialogTitle></DialogHeader>
          {action?.req && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                {action.req.item_name} — {action.req.qty_approved} {action.req.uom} approved for {action.req.requested_by_name} at {action.req.location_name}
              </p>
              <div>
                <Label>Issue From Location</Label>
                <Select value={issueLocationId} onValueChange={(v) => { setIssueLocationId(v); setIssueBatchId(""); }}>
                  <SelectTrigger data-testid="issue-location"><SelectValue placeholder="Select source location" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {locations.map((l) => <SelectItem key={l._id} value={l._id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {issueLocationId && (
                <div>
                  <Label>Batch / Lot</Label>
                  <div className="space-y-1.5 mt-1">
                    {issueBatches.length === 0 && <div className="text-sm text-slate-400">No stock available at this location.</div>}
                    {issueBatches.map((b) => (
                      <button
                        key={b._id}
                        type="button"
                        data-testid={`issue-batch-${b.is_fefo ? "fefo" : "other"}`}
                        onClick={() => setIssueBatchId(b._id)}
                        className="w-full flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-colors duration-200"
                        style={{
                          borderColor: issueBatchId === b._id ? "#042C53" : b.is_fefo ? "#10B981" : "#e2e8f0",
                          borderWidth: issueBatchId === b._id || b.is_fefo ? 2 : 1,
                          background: issueBatchId === b._id ? "#F0F9FF" : "#fff",
                        }}
                      >
                        <span className="font-mono text-xs font-semibold text-navy">{b.lot_number}</span>
                        {b.is_fefo && <FefoBadge />}
                        <ExpiryBadge state={b.expiry_state} days={b.days_to_expiry} />
                        <span className="text-xs text-slate-500 ml-auto">{b.qty_available} available</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label>Quantity to Issue</Label>
                <Input type="number" min={1} data-testid="issue-qty" value={issueQty} onChange={(e) => setIssueQty(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={doIssue} data-testid="issue-confirm" className="text-white" style={{ background: "#6366F1" }}>
              <PackageCheck size={16} className="mr-2" /> Issue Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
